import { IonIcon, IonLabel, useIonViewDidEnter } from '@ionic/react';
import { cardOutline, shieldCheckmark } from 'ionicons/icons';
import { ReactElement, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { User, useListUsersQuery } from '../../api/graphql/generated';
import { trackAccessedTenantUser } from '../../config/analytics';
import { useBeaconRouter } from '../../route/route.hooks';
import { Avatar } from '../avatar/avatar';
import { IonItem } from '../ionic/IonItem';
import { ObjectList, RowRenderer } from '../object/object-list';
import { getNameForUser } from '../team/team-users';
import { DateField } from '../ui/date-field';
import { ActiveFilterPopoverContent } from '../ui/searchbar/active-filter-popover-content';
import { SearchbarPopoverChildrenProps } from '../ui/searchbar/searchbar';

const TenantUsers = (): ReactElement => {
  const { i18n } = useTranslation();
  useIonViewDidEnter(() => {
    trackAccessedTenantUser();
  }, []);

  const { builder } = useBeaconRouter();
  const [showActive, setShowActive] = useState<boolean>(true);

  const filterPopoverContent = (props: SearchbarPopoverChildrenProps) => {
    return (
      <ActiveFilterPopoverContent
        showActive={showActive}
        onActiveChange={(active) => {
          setShowActive(active);
          // eslint-disable-next-line react/prop-types
          props.close();
        }}
      />
    );
  };

  const rowRenderer = useCallback<RowRenderer<User>>(
    ({ item: user }) => {
      const endSlotClass = user.active ? 'ion-hide-sm-down' : undefined;
      const rightElement =
        user.active !== true ? (
          i18n.t('common:inactive')
        ) : (
          <DateField
            date={user.activityAt}
            fallback={i18n.t('common:last_activity_never')}
          />
        );
      return (
        <IonItem
          className={user.active ? undefined : 'disabled-user'}
          key={user.id}
          routerLink={builder.getAdminUserRoute(user.id)}
        >
          <Avatar attachment={user?.avatarAttachment} />
          <IonLabel className="ion-padding-start">
            <h2>
              {getNameForUser(user)}
              {user.admin && (
                <IonIcon
                  style={{ paddingLeft: '6px', fontSize: '0.8em' }}
                  icon={shieldCheckmark}
                  color="primary"
                />
              )}
              {user.billingManager && (
                <IonIcon
                  style={{ paddingLeft: '6px', fontSize: '0.8em' }}
                  icon={cardOutline}
                  color="secondary"
                />
              )}
            </h2>
            <p>{user?.email}</p>
          </IonLabel>
          <IonLabel slot="end" className={endSlotClass}>
            <p>{rightElement}</p>
          </IonLabel>
        </IonItem>
      );
    },
    [builder, i18n]
  );

  return (
    <ObjectList
      query={{
        useQuery: useListUsersQuery,
        input: { active: showActive },
        queryOptions: {
          fetchPolicy: 'cache-and-network',
          nextFetchPolicy: 'cache-first',
        },
      }}
      filterPopoverContent={filterPopoverContent}
      showFilterIndicator={!showActive}
      noResults={i18n.t('common:user_no_users_found')}
      rowRenderer={rowRenderer}
    />
  );
};

export { TenantUsers };
